// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * session.ts — the multiplayer glue, deliberately thin.
 *
 * Every peer runs its OWN tower from the same frozen (seed, mode, roster), so no
 * physics state ever crosses the wire — which is essential, because rigid-body
 * physics is not reproducible across machines. Three tiny channels carry the
 * whole game:
 *
 *   st    status gossip ~4Hz — {h, placed, lost, alive} for the mini-towers
 *   gust  sabotage           — {to} (a crosswind for one peer's falling brick)
 *   res   a finished tower    — the full per-player breakdown
 *
 * The host owns exactly one thing: starting the round (via rematch.ts). There is
 * no authoritative simulation to inherit, so HOST TRANSFER IS A DISPLAY CONCERN
 * — the promoted peer already holds every result and just picks up the rematch
 * protocol.
 *
 * GUST ROUTING is sender-side: whoever earns a gust picks the current height
 * leader from its own gossip view and addresses the wind to them. No arbiter, so
 * a host leaving mid-round cannot stall it; two peers disagreeing about who
 * leads for a quarter-second costs one misdirected gust, not a hang.
 */

import type { Net, PeerId } from '@ben-gy/game-engine/net';
import type { Rounds } from '@ben-gy/game-engine/rematch';
import type { PlayerResult } from './runner';

export interface TowerStatus {
  h: number;
  placed: number;
  lost: number;
  alive: boolean;
}

/** Flat, small wire shape for a result. */
export interface WireResult {
  n: string;
  sc: number;
  hc: number;   // height cm
  hl: number;   // height cells
  dr: number;
  se: number;
  ls: number;
  wo: number;   // won (0/1)
}

export interface SessionHandlers {
  onStatus: (id: PeerId, st: TowerStatus) => void;
  onAttack: () => void;
  onResult: (id: PeerId, r: PlayerResult) => void;
}

export interface Session {
  sendStatus: (st: TowerStatus) => void;
  /** Address a gust at the current height leader among live opponents. */
  sendAttack: (seated: PeerId[]) => void;
  sendResult: (r: PlayerResult) => void;
  statusOf: (id: PeerId) => TowerStatus | undefined;
  reset: () => void;
  destroy: () => void;
}

export function toWire(r: PlayerResult): WireResult {
  return {
    n: r.name, sc: r.score, hc: r.heightCm, hl: r.heightCells,
    dr: r.drops, se: r.settled, ls: r.lost, wo: r.won ? 1 : 0,
  };
}

export function fromWire(id: PeerId, w: WireResult): PlayerResult {
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    id,
    name: typeof w?.n === 'string' && w.n.trim() ? w.n.slice(0, 24) : 'Player',
    score: num(w?.sc), heightCm: num(w?.hc), heightCells: num(w?.hl),
    drops: num(w?.dr), settled: num(w?.se), lost: num(w?.ls), won: w?.wo === 1,
    isSelf: false, isBot: false, left: false,
  };
}

export function createSession(net: Net, handlers: SessionHandlers): Session {
  const statuses = new Map<PeerId, TowerStatus>();

  const sendStatus = net.channel<TowerStatus>('st', (data, from) => {
    if (!data || typeof data !== 'object') return;
    const st: TowerStatus = {
      h: Number(data.h) || 0,
      placed: Number(data.placed) || 0,
      lost: Number(data.lost) || 0,
      alive: data.alive !== false,
    };
    statuses.set(from, st);
    handlers.onStatus(from, st);
  });

  const sendGustRaw = net.channel<{ to: PeerId }>('gust', (data) => {
    if (!data || data.to !== net.selfId) return;
    handlers.onAttack();
  });

  const sendResultRaw = net.channel<WireResult>('res', (data, from) => {
    if (!data || typeof data !== 'object') return;
    handlers.onResult(from, fromWire(from, data));
  });

  return {
    sendStatus,
    sendAttack(seated) {
      const live = seated.filter((id) => id !== net.selfId && (statuses.get(id)?.alive ?? true));
      if (live.length === 0) return;
      let target = live[0];
      let best = statuses.get(target)?.h ?? 0;
      for (const id of live) {
        const h = statuses.get(id)?.h ?? 0;
        if (h > best) { best = h; target = id; }
      }
      sendGustRaw({ to: target });
    },
    sendResult(r) { sendResultRaw(toWire(r)); },
    statusOf: (id) => statuses.get(id),
    reset() { statuses.clear(); },
    destroy() {
      sendStatus.off();
      sendGustRaw.off();
      sendResultRaw.off();
    },
  };
}

/**
 * The host's settings, frozen into the round start. Guests must render
 * `state().hostOpts`, never their own local pick.
 */
export function roundOptsOf(modeId: string): { mode: string } {
  return { mode: modeId };
}

export function hostModeId(rounds: Rounds, fallback: string): string {
  const opts = rounds.state().hostOpts as { mode?: unknown } | undefined;
  return typeof opts?.mode === 'string' ? opts.mode : fallback;
}